/* eslint-disable react-hooks/exhaustive-deps */
import { useToast } from '@chakra-ui/react';
import { ButtonComponent } from 'components/buttons/ButtonComponent';
import { TooltipStandard } from 'components/tooltip/TooltipStandard';
import { useContextPrevision } from 'pages/admin/demand/planning/context/ContextPrevision';
import {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useState,
} from 'react';

import { useContextPlanningByLevel } from '../../../context/ContextPlanningByLevel';
import {
	AnyHierarchyLevel,
	HierarchyType,
	IFilterHierarchy,
} from '../../../types/planning.types';
import { useFindHierachiesComplete } from '../hook/useFindHierachiesComplete';
import {
	IHierarchyFilterParent,
	IHierarchyRows,
} from '../hook/useMainHierarchyFilter';
import { HierarchyFilterMessages } from '../variables/hierarchyFilterMessages';
import {
	IContextHierarchyFilter,
	IDataCheckedItem,
	IIdsChecked,
	IProviderHierarchyFilterProps,
	IsCompleted,
} from './types/contextHierarchyFilter.type';
import {
	buildHierarchyByLevel,
	IHierarchyFilter,
} from './utils/generateHierarchyFilterBySetup';
const ContextHierarchyFilter = createContext<IContextHierarchyFilter>(
	{} as IContextHierarchyFilter
);

const generateId = (
	filterByParent?: IHierarchyFilterParent[],
	row?: IHierarchyRows,
	level?: AnyHierarchyLevel | null
) => {
	if (!level) return 'root:0';
	return [
		...(filterByParent || []),
		...(row ? [{ level, value: row.value }] : []),
	]
		.map(f => f.level + ':' + f.value)
		.join('-');
};
const startOpen: Record<string, boolean> = {
	[generateId()]: true,
};
export default function ProviderHierarchyFilter({
	children,
	planningFilterConfiguration,
	searchTerm,
}: IProviderHierarchyFilterProps) {
	const [idsChecked, setIdsChecked] = useState<IIdsChecked>(
		new Map<string, IDataCheckedItem>([])
	);
	const [quantityAllIds, setQuantityAllIds] = useState(0);
	const onSetQuantityAllIds = useCallback(
		(quantity: number) => {
			if (quantityAllIds === 0) {
				setQuantityAllIds(quantity);
			}
		},
		[quantityAllIds, setQuantityAllIds]
	);
	const [idsOpenChildren, setIdsOpenChildren] =
		useState<Record<string, boolean>>(startOpen);

	const { companyAreaId, cycleId, planningType } = useContextPrevision();

	const {
		filters,
		isUpdateMainHierarchyFilterId,
		setIsUpdateMainHierarchyFilterId,
		isApplyFiltersLoading,
	} = useContextPlanningByLevel();

	const body = useMemo(
		() => ({
			cycle_id: cycleId,
			hierarchy: planningFilterConfiguration?.main_filter || 'product',
			company_area_id: companyAreaId,
			filters_check: {
				product_ids: filters?.productIds || [],
				commercial_hierarchy_ids: filters?.commercialIds || [],
				customer_ids: filters?.customerIds || [],
			},
		}),
		[
			companyAreaId,
			cycleId,
			filters?.commercialIds,
			filters?.customerIds,
			filters?.productIds,
			planningFilterConfiguration?.main_filter,
		]
	);
	const { mutateAsync: findHierachiesComplete } = useFindHierachiesComplete(
		planningType,
		body
	);

	const [idsIsCompleted, setIdsIsCompleted] = useState<
		Map<number, IsCompleted>
	>(new Map<number, IsCompleted>());
	const getHierachiesComplete = useCallback(
		async (ids: number[], update: boolean) => {
			const newIdsIsCompleted = new Map(idsIsCompleted);
			if (!update) {
				ids = ids.filter(id => !newIdsIsCompleted.has(id));
			}
			if (ids.length > 0) {
				setIdsIsCompleted(pre => {
					const newIdsIsCompleted = new Map(pre);
					for (const id of ids) {
						newIdsIsCompleted.set(id, 'loading');
					}
					return newIdsIsCompleted;
				});
				const result = await findHierachiesComplete(ids);

				setIdsIsCompleted(pre => {
					const newIdsIsCompleted = new Map(pre);

					for (const item of result) {
						newIdsIsCompleted.set(item.id, item.completed);
					}
					return newIdsIsCompleted;
				});
			}
		},
		[findHierachiesComplete, idsIsCompleted]
	);
	const [keysEffect, setKeysEffect] = useState<Array<string | null>>([]);
	useEffect(() => {
		const generateUseEffectid = (
			keyHierarchy: HierarchyType,
			keyFilter: keyof IFilterHierarchy
		) => {
			const mainFilter = planningFilterConfiguration?.main_filter;
			return mainFilter !== keyHierarchy
				? keyFilter +
						': ' +
						(filters?.[keyFilter]?.length
							? filters?.[keyFilter]?.join(',')
							: '-')
				: null;
		};
		if (!isApplyFiltersLoading) {
			const key1 = generateUseEffectid('product', 'productIds');
			const key2 = generateUseEffectid('commercial', 'commercialIds');
			const key3 = generateUseEffectid('customer', 'customerIds');
			const [oldKey1, oldKey2, oldKey3] = keysEffect;
			if (key1 !== oldKey1 || key2 !== oldKey2 || key3 !== oldKey3) {
				setKeysEffect([key1, key2, key3]);
			}
		}
	}, [filters?.productIds, filters?.commercialIds, filters?.customerIds]);
	useEffect(() => {
		if (idsIsCompleted.size !== 0) {
			getHierachiesComplete(Array.from(idsIsCompleted.keys()), true);
		}
	}, [keysEffect]);
	useEffect(() => {
		if (
			!isApplyFiltersLoading &&
			isUpdateMainHierarchyFilterId !== undefined
		) {
			const ids: number[] = isUpdateMainHierarchyFilterId.length
				? isUpdateMainHierarchyFilterId
				: Array.from(idsIsCompleted.keys());
			setIsUpdateMainHierarchyFilterId(undefined);
			getHierachiesComplete(ids, true);
		}
	}, [
		getHierachiesComplete,
		isUpdateMainHierarchyFilterId,
		Array.from(idsIsCompleted.keys()),
	]);

	const toast = useToast();
	const changeOpenToItem = (id: string, open: boolean) => {
		setIdsOpenChildren(pre => ({
			...pre,
			[id]: open,
		}));
	};
	const onChangeChecked = (
		ids: number[],
		checked: boolean,
		updateParents?: (idsChecked: IIdsChecked) => void,
		updateChildren?: (idsChecked: IIdsChecked) => void
	) => {
		setIdsChecked(pre => {
			const newMap = new Map(pre);
			for (const id of ids) {
				if (checked) {
					newMap.set(String(id), {
						id,
					});
				} else {
					newMap.delete(String(id));
				}
			}
			if (updateParents || updateChildren) {
				updateParents?.(newMap);
				updateChildren?.(newMap);
			}
			return newMap;
		});
	};
	const { hierarchiesFilterMain, hierarchyFilterMain } = useMemo(() => {
		const result = buildHierarchyByLevel(planningFilterConfiguration);

		return result;
	}, [planningFilterConfiguration]);
	const { applyFilter } = useContextPlanningByLevel();

	const applyMainFilter = () => {
		if (!planningFilterConfiguration) return;
		const quantityCheckedItems = idsChecked.size;
		const allCheckedItems = quantityCheckedItems === quantityAllIds;
		const mainFilter = planningFilterConfiguration.main_filter;
		const ids =
			!allCheckedItems && quantityAllIds > 0
				? Array.from(idsChecked.values()).map(item => item.id)
				: undefined;
		applyFilter({
			[mainFilter]: {
				logicalOperator: 'or',
				ids,
			},
		});
		toast({
			title: 'Filtro aplicado com sucesso!',
			status: 'success',
			position: 'bottom-left',
			duration: 2000,
			isClosable: true,
		});
	};
	const buttonApplyFilter = () => (
		<TooltipStandard label={HierarchyFilterMessages.MSG_APPLY_TOOLTIP}>
			<ButtonComponent
				mt={4}
				width='100%'
				type='ghost'
				title={HierarchyFilterMessages.MSG_APPLY}
				onClick={applyMainFilter}
				_hover={{ bg: 'easyBLUE.100' }}
			/>
		</TooltipStandard>
	);
	return (
		<ContextHierarchyFilter.Provider
			value={{
				onChangeChecked,
				onSetQuantityAllIds,
				idsChecked,
				generateId,
				hierarchiesFilterMain,
				hierarchy: planningFilterConfiguration?.main_filter,
				hierarchyFilterMain: hierarchyFilterMain as IHierarchyFilter,
				idsOpenChildren,
				changeOpenToItem,
				buttonApplyFilter,
				hierarchyType: planningFilterConfiguration?.main_filter,
				searchTerm,
				getHierachiesComplete,
				idsIsCompleted,
			}}
		>
			{children}
		</ContextHierarchyFilter.Provider>
	);
}

export const useContextHierarchyFilter = () => {
	return useContext(ContextHierarchyFilter);
};
